import React, { useRef, useState } from 'react';
import FileIcon from '../../../icons/FileIcon';
import DownloadIcon from '../../../icons/DownloadIcon';
import TrashIcon from '../../../icons/TrashIcon';

export interface MeetingFile {
  file_id: string;
  filename: string;
  file_size: number;
  status: string | number;
  chunk_count?: number | null;
}

// Backend FileStatus enum order, used if status arrives as an integer
const FILE_STATUS_NAMES = ['uploaded', 'processing', 'processed', 'failed', 'queued'];

const normalizeStatus = (status: string | number | null | undefined): string => {
  if (typeof status === 'number') return FILE_STATUS_NAMES[status] ?? 'unknown';
  if (typeof status === 'string') return status.toLowerCase();
  return 'unknown';
};

// User-facing labels for internal file statuses
const STATUS_LABELS: Record<string, string> = {
  uploaded: 'Uploaded',
  queued: 'Waiting',
  processing: 'Analyzing...',
  processed: 'Ready',
  failed: 'Failed'
};

interface MeetingMaterialsPanelProps {
  files: MeetingFile[];
  isUploading: boolean;
  // Secretary/Admin: may upload and delete materials. Others may only download.
  canManage?: boolean;
  onUpload: (files: File[]) => void;
  onDownload: (file: MeetingFile) => void;
  onDelete: (file: MeetingFile) => Promise<void>;
  // Opens the material in the reader. Omitted, rows stay download-only.
  onOpen?: (file: MeetingFile) => void;
  // fileId -> the reader's own prep-note count, shown per row so a member can
  // see where they have already prepared without opening each document.
  commentCounts?: Record<string, number>;
}

const formatSize = (bytes: number): string => {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${bytes} B`;
};

const MeetingMaterialsPanel: React.FC<MeetingMaterialsPanelProps> = ({ files, isUploading, canManage = false, onUpload, onDownload, onDelete, onOpen, commentCounts }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const handleDelete = async (file: MeetingFile) => {
    setDeletingId(file.file_id);
    try {
      await onDelete(file);
    } finally {
      setDeletingId(null);
    }
  };

  const handleFiles = (fileList: FileList | null) => {
    if (fileList && fileList.length > 0) {
      onUpload(Array.from(fileList));
    }
  };

  return (
    <div className="meeting-panel">
      <div
        className="meeting-panel-header meeting-panel-header-collapsible"
        role="button"
        aria-expanded={!collapsed}
        onClick={() => setCollapsed((c) => !c)}
      >
        <div>
          <h3 className="meeting-panel-title">
            Materials{files.length > 0 ? ` (${files.length})` : ''}
          </h3>
          <p className="meeting-panel-subtitle">
            Documents ground the agenda, briefing, and chat
          </p>
        </div>
        <span className={`meeting-panel-chevron ${collapsed ? '' : 'expanded'}`}>▾</span>
      </div>

      {!collapsed && (
      <>
      {canManage && (
      <div
        className={`meeting-upload-drop ${dragOver ? 'drag-over' : ''}`}
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          handleFiles(e.dataTransfer.files);
        }}
      >
        {isUploading ? (
          <div className="meeting-generating" style={{ justifyContent: 'center' }}>
            <span className="spinner" /> Uploading and processing...
          </div>
        ) : (
          <>Drop files here or click to upload (PDF, DOCX, XLSX, TXT...)</>
        )}
        <input
          ref={inputRef}
          type="file"
          multiple
          hidden
          onChange={(e) => {
            handleFiles(e.target.files);
            e.target.value = '';
          }}
        />
      </div>
      )}

      {files.length === 0 ? (
        <div className="meeting-empty-hint">No materials uploaded yet.</div>
      ) : (
        <div>
          {files.map((f) => {
            const status = normalizeStatus(f.status);
            const isDeleting = deletingId === f.file_id;
            const noteCount = commentCounts?.[f.file_id] ?? 0;
            return (
              <div className="meeting-file-row" key={f.file_id}>
                <div className="meeting-file-info">
                  <FileIcon size={18} />
                  {onOpen ? (
                    <button
                      className="meeting-file-name meeting-file-name-open"
                      title={`Read ${f.filename}`}
                      onClick={() => onOpen(f)}
                    >
                      {f.filename}
                    </button>
                  ) : (
                    <span className="meeting-file-name" title={f.filename}>{f.filename}</span>
                  )}
                  <span className="meeting-file-meta">{formatSize(f.file_size)}</span>
                  {noteCount > 0 && (
                    <span
                      className="meeting-file-notes"
                      title={`${noteCount} of your prep note${noteCount === 1 ? '' : 's'}`}
                      aria-label={`${noteCount} of your prep note${noteCount === 1 ? '' : 's'}`}
                    >
                      ✎ {noteCount}
                    </span>
                  )}
                </div>
                <div className="meeting-file-actions">
                  <span className={`meeting-file-status ${status}`}>
                    {STATUS_LABELS[status] ?? status}
                  </span>
                  <button
                    className="meeting-file-action-btn"
                    title="Download"
                    aria-label={`Download ${f.filename}`}
                    onClick={() => onDownload(f)}
                  >
                    <DownloadIcon size={18} />
                  </button>
                  {canManage && (
                    <button
                      className="meeting-file-action-btn meeting-file-action-danger"
                      title="Delete"
                      aria-label={`Delete ${f.filename}`}
                      disabled={isDeleting}
                      onClick={() => handleDelete(f)}
                    >
                      {isDeleting ? <span className="spinner spinner-sm" /> : <TrashIcon size={18} />}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
      </>
      )}
    </div>
  );
};

export default MeetingMaterialsPanel;
